/**
 * Correspondencia entre los códigos de FACe y el estado de la app.
 *
 * Códigos de tramitación (`factura.tramitacion.codigo`): 1200 registrada,
 * 1300 registrada en RCF, 2400 contabilizada la obligación de pago, 2500 pagada,
 * 2600 rechazada, 3100 anulada. Códigos de anulación (`factura.anulacion.codigo`):
 * 4100 no solicitada, 4200 solicitada, 4300 aceptada, 4400 rechazada.
 */
import type {FaceEstado} from "./types.js";

const ESTADO_POR_TRAMITACION: Record<string, FaceEstado> = {
  "1200": "REGISTRADA",
  "1300": "REGISTRADA",
  "2400": "CONTABILIZADA",
  "2500": "PAGADA",
  "2600": "RECHAZADA",
  "3100": "ANULADA",
};

/** La anulación aceptada manda sobre el estado de tramitación. */
const ANULACION_ACEPTADA = "4300";

/** Estados de los que una factura ya no sale en FACe: dejan de consultarse. */
const ESTADOS_FINALES: FaceEstado[] = ["PAGADA", "RECHAZADA", "ANULADA"];

/**
 * Estado de la app para los códigos que devuelve FACe, o `undefined` si el
 * código no se conoce: quien llama conserva entonces el estado anterior.
 */
export function estadoDesdeCodigoFace(codigoTramitacion: string, codigoAnulacion?: string): FaceEstado | undefined {
  if (codigoAnulacion === ANULACION_ACEPTADA) return "ANULADA";
  return ESTADO_POR_TRAMITACION[codigoTramitacion];
}

/**
 * Indica si la factura ha llegado a un estado del que ya no va a salir en FACe.
 * La sincronización programada deja de consultar las facturas en estado final;
 * las demás se consultan en cada pasada.
 *
 * - `estado`: estado de la app ya traducido con `estadoDesdeCodigoFace`
 *   (`undefined` si FACe devolvió un código que no conocemos).
 * - `codigoAnulacion`: código de anulación de FACe (4100 no solicitada,
 *   4200 solicitada, 4300 aceptada, 4400 rechazada), si lo hay.
 */
export function esEstadoFinal(estado: FaceEstado | undefined, codigoAnulacion?: string): boolean {
  // Decisión del usuario: Contabilizada no es final (se sigue hasta el pago), y
  // tampoco una anulación solicitada o rechazada ni un código desconocido.
  if (codigoAnulacion === ANULACION_ACEPTADA) return true;
  return estado !== undefined && ESTADOS_FINALES.includes(estado);
}
